export const OPPORTUNITY_LIMITS = { taken: 999, moved: 64 };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isId = (v) => typeof v === 'string' && UUID_RE.test(v);
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
function parseCastingNow(v) {
    if (!isObj(v) || typeof v.spell !== 'string' || !v.spell.length || v.spell.length > 120)
        return null;
    if (v.level !== undefined && !(Number.isInteger(v.level) && v.level >= 0 && v.level <= 100))
        return null;
    if (v.area !== undefined && !isId(v.area))
        return null;
    return { spell: v.spell, ...(v.level !== undefined ? { level: v.level } : {}), ...(v.area ? { area: v.area } : {}), ...(v.long === true ? { long: true } : {}) };
}
export function parseOpportunityBody(b) {
    if (!isId(b.id) || (b.taken !== undefined && b.taken !== true) || (b.provoker !== undefined && !isId(b.provoker)))
        return null;
    const casting = b.casting === undefined || b.casting === null ? null : parseCastingNow(b.casting);
    if (b.casting !== undefined && b.casting !== null && !casting)
        return null;
    return { kind: 'encounter.opportunity', id: b.id, ...(b.taken ? { taken: true } : {}), ...(b.provoker ? { provoker: b.provoker } : {}), ...(b.casting !== undefined ? { casting } : {}) };
}
/** The tally as a snapshot holds it, read strictly; absent when there is none. */
export function normaliseOpportunities(v) {
    if (!isObj(v))
        return undefined;
    const out = {};
    for (const [id, t] of Object.entries(v)) {
        if (!isId(id) || !isObj(t) || !Number.isInteger(t.taken) || t.taken < 0 || !Array.isArray(t.moved))
            continue;
        const casting = t.casting === undefined ? null : parseCastingNow(t.casting);
        out[id] = { taken: Math.min(t.taken, OPPORTUNITY_LIMITS.taken), moved: t.moved.filter(isId).slice(-OPPORTUNITY_LIMITS.moved), ...(casting ? { casting } : {}) };
    }
    return Object.keys(out).length ? out : undefined;
}
/** What a creature has used since its turn. */
export const tallyOf = (enc, id) => enc.opportunities?.[id] ?? { taken: 0, moved: [] };
export function reduceOpportunity(enc, body) {
    const t = tallyOf(enc, body.id);
    const moved = body.provoker && !t.moved.includes(body.provoker) ? [...t.moved, body.provoker].slice(-OPPORTUNITY_LIMITS.moved) : t.moved;
    const { casting: was, ...kept } = t;
    const casting = body.casting === undefined ? was : body.casting ?? undefined;
    return { ...enc, opportunities: { ...enc.opportunities, [body.id]: { ...kept, taken: Math.min(t.taken + (body.taken ? 1 : 0), OPPORTUNITY_LIMITS.taken), moved, ...(casting ? { casting } : {}) } } };
}
/**
 * How many a creature may take in a round: the system's `count`, and for each of `adds` it carries, the sheet or stat
 * block number named, when above 0 (a bonus: a low score takes nothing away).
 */
export function opportunityLimit(rule, has, numbers) {
    let limit = rule.count;
    for (const a of rule.adds) {
        if (!has(a.feat))
            continue;
        const words = a.which.split('_');
        const n = numbers.find((x) => x.purpose === a.purpose && words.every((w) => x.words.includes(w)));
        limit += Math.max(0, Math.trunc(n?.value ?? 0));
    }
    return limit;
}
/**
 * Whether a reaction's record has just become an attack taken: its first answer carried on, past the question, or it asked
 * nothing and threw. A No ends it with nothing thrown; later answers of the same record are not another attack.
 */
export function opportunityTaken(t) {
    if (t.stopped || t.answers.length > 1)
        return false;
    return t.answers.length === 1 ? t.ask !== undefined || t.faces.length > 0 : t.ask === undefined && t.faces.length > 0;
}
//# sourceMappingURL=opportunity.js.map